import styled from "styled-components";
import { useState, useEffect, useRef } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import createGeodesicGeojson from "@/utils/geodesic_geojson/create_geodesic_geojson";

const mapboxgl = require("mapbox-gl/dist/mapbox-gl.js");
mapboxgl.accessToken =
	"pk.eyJ1IjoiYXZpYW5zb2x1dGlvbnMiLCJhIjoiY2xrZ2xjZnAwMDFjOTNxbXFkenVmY3hnYyJ9.5-2RXVFzTzjOtch38cpqcQ";

export default function getPolylineBounds(bounds) {
	return bounds.reduce((processArray, bound) => {
		const topLeftLat = Array.isArray(processArray[0]) ? processArray[0][0] : processArray[0];
		const topLeftLng = Array.isArray(processArray[0]) ? processArray[0][1] : processArray[1];
		const bottomRightLat = Array.isArray(processArray[1]) ? processArray[1][0] : processArray[0];
		const bottomRightLng = Array.isArray(processArray[1]) ? processArray[1][1] : processArray[1];

		const topLeft = [topLeftLat < bound[0] ? topLeftLat : bound[0], topLeftLng < bound[1] ? topLeftLng : bound[1]];
		const bottomRight = [
			bottomRightLat > bound[0] ? bottomRightLat : bound[0],
			bottomRightLng > bound[1] ? bottomRightLng : bound[1],
		];

		return [topLeft, bottomRight];
	});
}

function equalsCheck(a = [], b = []) {
	if (a.length != b.length) {
		return false;
	} else {
		let result = false;
		// comparing each element of array
		for (let i = 0; i < a.length; i++) {
			if (
				a[i]?.from_airport?._id !== b[i]?.from_airport?._id ||
				a[i]?.to_airport?._id !== b[i]?.to_airport?._id
			) {
				return false;
			} else {
				result = true;
			}
		}
		return result;
	}
}

export function RAMap({ trip = [] }) {
	const [map, setMap] = useState();
	const [mapTrips, setMapTrips] = useState([]);
	const mapNode = useRef(null);

	useEffect(() => {
		if (trip && trip?.length > 0) {
			let values = [];
			trip.forEach((element) => {
				if (element?.from_airport?._id && element?.to_airport?._id) {
					values.push({
						from_airport: element.from_airport,
						to_airport: element?.to_airport,
					});
				}
			});
			// check routes have change
			const result = equalsCheck(values, mapTrips);
			if (!result) {
				setMapTrips(values);
			}
		}
	}, [trip]);

	useEffect(() => {
		const node = mapNode.current;
		// if the window object is not found, that means
		// the component is rendered on the server
		// or the dom node is not initialized, then return early
		if (typeof window === "undefined" || node === null) return;

		// otherwise, create a map instance
		const mapboxMap = new mapboxgl.Map({
			container: node,
			style: "mapbox://styles/mapbox/light-v11",
			center: [-92.5, 40],
			zoom: 3,
			bearing: 0,
			pitch: 0,
			attributionControl: false,
		});

		mapboxMap.dragRotate.disable();
		mapboxMap.touchZoomRotate.disableRotation();

		// const geolocateControl = new mapboxgl.GeolocateControl({
		//     positionOptions: { enableHighAccuracy: true },
		//     showUserHeading: true,
		//     trackUserLocation: true,
		// })
		// mapboxMap.addControl(geolocateControl)
		// mapboxMap.addControl(new mapboxgl.FullscreenControl())
		mapboxMap.addControl(new mapboxgl.NavigationControl(), "bottom-right");

		let features = [];
		let polygonFeatures = [];
		if (mapTrips && mapTrips?.length > 0) {
			mapboxMap.on("load", () => {
				// Add an image to use as a custom marker
				// Add a GeoJSON source with 2 points
				mapTrips.map((item = {}) => {
					const { from_airport = {}, to_airport = {} } = item;

					if (from_airport?.location?.geometry?.coordinates) {
						polygonFeatures.push(from_airport?.location?.geometry?.coordinates);
						features.push({
							type: "Feature",
							geometry: {
								type: "Point",
								coordinates: from_airport?.location?.geometry?.coordinates,
							},
							properties: {
								title: from_airport?.icao_code || from_airport?.iata_code,
							},
						});
					}
					if (to_airport?.location?.geometry?.coordinates) {
						polygonFeatures.push(to_airport?.location?.geometry?.coordinates);
						features.push({
							type: "Feature",
							geometry: {
								type: "Point",
								coordinates: to_airport?.location?.geometry?.coordinates,
							},
							properties: {
								title: to_airport?.icao_code || to_airport?.iata_code,
							},
						});
					}
				});
				mapboxMap.addSource("points", {
					type: "geojson",
					data: {
						type: "FeatureCollection",
						features: features,
					},
				});

				// add marker
				// features.forEach(function (marker, i) {
				//     // create a HTML element for each feature
				//     var el = document.createElement("div")
				//     el.className = "marker"
				//     el.innerHTML = ReactDOMServer.renderToString(
				//         <IconMarkerSvg />,
				//     )

				//     // make a marker for each feature and add it to the map
				//     new mapboxgl.Marker(el)
				//         .setLngLat(marker.geometry.coordinates)
				//         .addTo(mapboxMap)
				// })

				// Add a symbol layer
				mapboxMap.addLayer({
					id: "points",
					type: "symbol",
					source: "points",
					layout: {
						"icon-image": "custom-marker",
						// get the title name from the source's "title" property
						"text-field": ["get", "title"],
						"text-padding": 0,
						"icon-allow-overlap": true,
						"text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
						"text-size": 14,
						"text-transform": "uppercase",
						"text-letter-spacing": 0.05,
						"text-offset": [0, 1.5],
					},
					paint: {
						"text-color": "#fff",
						"text-halo-color": "#FFB11A",
						"text-halo-width": 30,
					},
				});
				mapboxMap.addSource("route2", {
					type: "geojson",
					data: createGeodesicGeojson({
						type: "Feature",
						properties: {},
						geometry: {
							type: "LineString",
							coordinates: polygonFeatures,
						},
					})[0],
				});
				mapboxMap.addSource("route", {
					type: "geojson",
					data: {
						type: "Feature",
						properties: {},
						geometry: {
							type: "LineString",
							coordinates: polygonFeatures,
						},
					},
				});
				// add the line which will be modified in the animation
				mapboxMap.addLayer({
					id: "route",
					type: "line",
					source: "route2",
					layout: {
						"line-join": "round",
						"line-cap": "round",
					},
					paint: {
						"line-color": "#FFB11A",
						"line-width": 2,
					},
				});
				mapboxMap.addLayer({
					id: "my-lines",
					type: "circle",
					source: "route",
					paint: {
						"circle-color": "#FFB11A",
						"circle-radius": 5,
					},
				});

				/*
				 *  `fitBounds()` zooms and pans
				 *  the viewport to contain a bounding box that surrounds lines.
				 *  corners of the specified geographical bounds.
				 */
				if (polygonFeatures?.length > 0) {
					// let bounds = getPolylineBounds(polygonFeatures);

					const bounds = new mapboxgl.LngLatBounds();
					bounds.extend(polygonFeatures[0]);
					bounds.extend(polygonFeatures[1]);

					const camera = mapboxMap.cameraForBounds(bounds, {
						padding: 40, // hoặc { top, right, bottom, left }
						// bearing: map.getBearing(),
						// pitch: map.getPitch(),
					});

					camera.zoom = camera.zoom - 1;

					mapboxMap.easeTo({
						...camera,
						duration: 800,
					});
				}
			});
		}

		// save the map object to React.useState
		setMap(mapboxMap);

		return () => {
			mapboxMap.remove();
		};
	}, [mapTrips]);

	return <WrapperMap ref={mapNode} />;
}

const WrapperMap = styled.div`
	margin-top: 12px;
	width: 100%;
	height: 320px;
	border-radius: 8px;
	border: 1px solid #e9e8e8;
	.mapboxgl-ctrl-logo {
		display: none !important;
	}
	.mapboxgl-ctrl-bottom-left .mapboxgl-ctrl-logo {
		display: none !important;
	}
	.mapboxgl-ctrl-scale {
		border: 1px solid #e7e7e7;
		margin-bottom: 0;
		color: #222222;
		padding: 0 4px;
		width: auto !important;
		border-radius: 4px;
	}
	.mapboxgl-ctrl button.mapboxgl-ctrl-fullscreen .mapboxgl-ctrl-icon {
		background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23222222%22%0A%20%20%20%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20class%3D%22feather%20feather-maximize%22%3E%0A%20%20%20%20%3Cpath%20d%3D%22M8%203H5a2%202%200%200%200-2%202v3m18%200V5a2%202%200%200%200-2-2h-3m0%2018h3a2%202%200%200%200%202-2v-3M3%2016v3a2%202%200%200%200%202%202h3%22%3E%3C%2Fpath%3E%0A%3C%2Fsvg%3E");
	}
	.mapboxgl-ctrl button.mapboxgl-ctrl-shrink .mapboxgl-ctrl-icon {
		background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23222222%22%0A%20%20%20%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20class%3D%22feather%20feather-minimize%22%3E%0A%20%20%20%20%3Cpath%20d%3D%22M8%203v3a2%202%200%200%201-2%202H3m18%200h-3a2%202%200%200%201-2-2V3m0%2018v-3a2%202%200%200%201%202-2h3M3%2016h3a2%202%200%200%201%202%202v3%22%3E%3C%2Fpath%3E%0A%3C%2Fsvg%3E");
	}
	.mapboxgl-ctrl button.mapboxgl-ctrl-zoom-in .mapboxgl-ctrl-icon {
		background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23222222%22%0A%20%20%20%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20class%3D%22feather%20feather-plus%22%3E%0A%20%20%20%20%3Cline%20x1%3D%2212%22%20y1%3D%225%22%20x2%3D%2212%22%20y2%3D%2219%22%3E%3C%2Fline%3E%0A%20%20%20%20%3Cline%20x1%3D%225%22%20y1%3D%2212%22%20x2%3D%2219%22%20y2%3D%2212%22%3E%3C%2Fline%3E%0A%3C%2Fsvg%3E");
	}
	.mapboxgl-ctrl button.mapboxgl-ctrl-zoom-out .mapboxgl-ctrl-icon {
		background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23222222%22%0A%20%20%20%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20class%3D%22feather%20feather-minus%22%3E%0A%20%20%20%20%3Cline%20x1%3D%225%22%20y1%3D%2212%22%20x2%3D%2219%22%20y2%3D%2212%22%3E%3C%2Fline%3E%0A%3C%2Fsvg%3E");
	}
`;
